/*
 * Tykables — Back-in-Stock Notify-Me
 * -----------------------------------------------------------------------------
 * Custom element <tyk-notify-me> rendered by sections/tyk-main-product.liquid
 * when the current variant is out of stock AND the customer is logged in.
 * (Anonymous users see a "Log in to get notified" link instead of this element,
 * so JS doesn't need to handle the unauthenticated path.)
 *
 * Flow on click:
 *   1. POST { variant_id, action: 'subscribe' | 'unsubscribe' } to the App
 *      Proxy /apps/notify endpoint. The app side writes to the customer's
 *      `custom.notify_variant_ids` metafield — that list is what the
 *      inventory-restock webhook scans to send the EMAIL notification.
 *   2. In parallel (and only on subscribe), call OneSignal.User.addTag(
 *      'notify_v_<variant_id>', '1') so the inventory-restock webhook
 *      can target a OneSignal segment for the PUSH notification. Tag is
 *      removed on unsubscribe.
 *   3. Swap the button to the opposite state on success; surface a
 *      friendly toast in the [data-tyk-notify-msg] region on failure.
 *
 * The two channels (email metafield + OneSignal tag) intentionally share a
 * single click so the user only has to opt in once and gets both. If
 * OneSignal isn't initialized (push disabled in theme settings, App ID
 * missing, etc.) we still write the metafield so email notifications work.
 *
 * Push permission is requested by OneSignal's existing soft-prompt flow on
 * first interaction — we don't trigger the native browser prompt directly,
 * because doing so without the soft pre-prompt is a Chrome anti-pattern that
 * burns the permission for the whole site if the user clicks Block.
 *
 * See docs/BACK-IN-STOCK.md for the API contract this script depends on.
 */
(function () {
  'use strict';

  if (customElements.get('tyk-notify-me')) return;

  customElements.define(
    'tyk-notify-me',
    class TykNotifyMe extends HTMLElement {
      connectedCallback() {
        this.toggleBtn = this.querySelector('[data-tyk-notify-toggle]');
        this.label = this.querySelector('[data-tyk-notify-label]');
        this.msg = this.querySelector('[data-tyk-notify-msg]');

        // The element renders without a toggle button when the customer
        // isn't logged in (the Liquid template emits a login link instead).
        // Bail in that case — there's nothing to bind.
        if (!this.toggleBtn) return;

        this.variantId = this.dataset.variantId;
        this.productHandle = this.dataset.productHandle;
        this.productTitle = this.dataset.productTitle;
        this.variantTitle = this.dataset.variantTitle;
        this.customerId = this.dataset.customerId;
        // Routed through the existing /apps/pwa App Proxy on the
        // tykables-theme Shopify app — that's the same proxy that serves
        // the PWA service worker. A single Cloudflare Worker handles both
        // (see workers/backend.js in the app project, deployed per
        // docs/DEPLOY-BACK-IN-STOCK.md).
        this.endpoint = this.dataset.endpoint || '/apps/pwa/notify';

        this._onClick = this._onClick.bind(this);
        this.toggleBtn.addEventListener('click', this._onClick);
      }

      disconnectedCallback() {
        if (this.toggleBtn && this._onClick) {
          this.toggleBtn.removeEventListener('click', this._onClick);
        }
      }

      get isSubscribed() {
        return this.dataset.subscribed === 'true';
      }

      set isSubscribed(value) {
        this.dataset.subscribed = value ? 'true' : 'false';
      }

      _onClick(evt) {
        evt.preventDefault();
        if (this.toggleBtn.classList.contains('is-loading')) return;

        var subscribing = !this.isSubscribed;
        this._setLoading(true);

        // Both channels are best-effort. If push fails (user denied,
        // OneSignal not loaded, etc.) we still want the metafield write to
        // succeed so the email path works. We surface a single error only
        // if the metafield write itself fails — the source of truth.
        var metafieldP = this._writeMetafield(subscribing);
        var pushP = this._updatePushTag(subscribing).catch(function (err) {
          // Non-fatal — email still works. Log for diagnostics.
          // eslint-disable-next-line no-console
          console.warn('[tyk-notify-me] push tag update failed:', err);
        });

        var self = this;
        Promise.all([metafieldP, pushP])
          .then(function () {
            self.isSubscribed = subscribing;
            self._renderState();
            self._showMsg(
              subscribing
                ? "We'll let you know the moment it's back."
                : "Notifications turned off for this size.",
              'ok'
            );
          })
          .catch(function (err) {
            // eslint-disable-next-line no-console
            console.error('[tyk-notify-me] subscribe failed:', err);
            self._showMsg(
              "Couldn't save your preference. Please try again.",
              'err'
            );
          })
          .finally(function () {
            self._setLoading(false);
          });
      }

      // -- Channel: email (customer metafield via App Proxy) ---------------
      _writeMetafield(subscribing) {
        return fetch(this.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            variant_id: this.variantId,
            product_id: this.dataset.productId,
            product_handle: this.productHandle,
            product_title: this.productTitle,
            variant_title: this.variantTitle,
            action: subscribing ? 'subscribe' : 'unsubscribe',
          }),
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json().catch(function () { return {}; });
        });
      }

      // -- Channel: push (OneSignal tag) -----------------------------------
      _updatePushTag(subscribing) {
        var variantId = this.variantId;
        var tagKey = 'notify_v_' + variantId;

        // OneSignalDeferred is set up by assets/onesignal-init.js. If push
        // is disabled in theme settings, the deferred queue won't drain,
        // so we resolve immediately and the tag write is a no-op.
        return new Promise(function (resolve, reject) {
          if (typeof window.OneSignalDeferred === 'undefined') {
            return resolve(); // push not initialized — email-only path
          }
          window.OneSignalDeferred.push(async function (OneSignal) {
            try {
              if (subscribing) {
                // Adding the tag implicitly opts the user into push if
                // they haven't already accepted. OneSignal's own soft
                // prompt (configured in onesignal-init.js) handles the
                // permission UI — we don't call requestPermission here
                // because doing so without the pre-prompt burns the
                // permission permanently if the user clicks Block.
                await OneSignal.User.addTag(tagKey, '1');
              } else {
                await OneSignal.User.removeTag(tagKey);
              }
              resolve();
            } catch (err) {
              reject(err);
            }
          });
        });
      }

      // -- UI helpers ------------------------------------------------------
      _setLoading(loading) {
        this.toggleBtn.classList.toggle('is-loading', loading);
        this.toggleBtn.disabled = loading;
      }

      _renderState() {
        if (this.isSubscribed) {
          this.toggleBtn.classList.add('is-subscribed');
          this.toggleBtn.classList.remove('tbtn--blue');
          this.toggleBtn.classList.add('tbtn--white');
          this.toggleBtn.setAttribute('aria-pressed', 'true');
          if (this.label) this.label.textContent = "✓ You'll be notified";
        } else {
          this.toggleBtn.classList.remove('is-subscribed');
          this.toggleBtn.classList.remove('tbtn--white');
          this.toggleBtn.classList.add('tbtn--blue');
          this.toggleBtn.setAttribute('aria-pressed', 'false');
          if (this.label) this.label.textContent = '🔔 Notify me when back';
        }
      }

      _showMsg(text, kind) {
        if (!this.msg) return;
        this.msg.textContent = text;
        this.msg.classList.remove('is-ok', 'is-err');
        if (kind === 'ok') this.msg.classList.add('is-ok');
        if (kind === 'err') this.msg.classList.add('is-err');
        this.msg.hidden = false;
        // Auto-dismiss success after 4s; keep errors visible.
        if (kind === 'ok') {
          var msg = this.msg;
          clearTimeout(this._msgT);
          this._msgT = setTimeout(function () {
            msg.hidden = true;
          }, 4000);
        }
      }
    }
  );
})();
